/**
 * Publishing this browser's identity to the key directory.
 *
 * Until these calls succeed the browser is invisible to every other device:
 * a sender resolving "who are the recipients" queries `/v1/keys/query` and
 * will not include a device that never published. That is why bootstrap runs
 * before anything tries to read a message, not lazily on first decrypt.
 */
import { api } from '../lib/api';
import { getDeviceId } from '../lib/device';
import { loadCrypto } from '@nsibi/e2ee-wasm';
import { getSession } from '../lib/session';
import { loadAccount, pickleKey, saveAccount } from './store';

/**
 * Canonical JSON: recursively sorted keys.
 *
 * Both ends must compute the same bytes or the signature is meaningless, so
 * this has to match `canonicalJson` in the mobile client exactly. Plain
 * `JSON.stringify` is not enough - it preserves insertion order, and two
 * clients building the same object in a different order would sign different
 * bytes and each conclude the other was forged.
 */
function canonicalJson(value: unknown): string {
  return JSON.stringify(sortKeys(value));
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>).sort()) {
      out[k] = sortKeys((value as Record<string, unknown>)[k]);
    }
    return out;
  }
  return value;
}

/** How many prekeys to keep in the pool. Each one lets a sender start a
 *  session with this browser while it is offline. */
const ONE_TIME_KEY_COUNT = 50;

export type Identity = { deviceId: string; curve25519: string; ed25519: string };

/**
 * This browser's public identity, WITHOUT publishing it.
 *
 * Needed before sign-in: the QR has to carry these keys so the phone can
 * Olm-encrypt room keys straight to this device the moment it approves.
 * Publishing needs a token, and at QR time there is no token yet - but the
 * account can exist, and the device id is already stable.
 */
export async function localIdentity(): Promise<Identity> {
  const core = await loadCrypto();
  core.setPickleKey(await pickleKey());

  let pickle = await loadAccount();
  if (!pickle) {
    pickle = core.createAccount();
    await saveAccount(pickle);
  }
  const keys = core.accountIdentityKeys(pickle);
  return { deviceId: getDeviceId(), curve25519: keys.curve25519, ed25519: keys.ed25519 };
}

/**
 * Load or create this browser's Olm account and publish its public keys.
 *
 * Idempotent: the account pickle is created once and reused, and the upload
 * endpoint is keyed on (user, device_id) so re-publishing just refreshes.
 */
export async function bootstrapIdentity(): Promise<Identity> {
  const core = await loadCrypto();
  core.setPickleKey(await pickleKey());

  let pickle = await loadAccount();
  if (!pickle) {
    pickle = core.createAccount();
    await saveAccount(pickle);
  }

  const deviceId = getDeviceId();
  const identity = core.accountIdentityKeys(pickle);

  // The signed body and the signatures map must match the phone's scheme
  // byte for byte, or a device that verifies before starting a session will
  // reject this browser. The map is NESTED - external user id, then
  // "ed25519:<deviceId>" - not flat.
  const deviceBody = {
    device_id: deviceId,
    curve25519_key: identity.curve25519,
    ed25519_key: identity.ed25519,
  };
  // The numeric user id, never the external id - the phone's reason: this
  // map is readable by anyone who queries our keys, and a phone account's
  // external id is the hash of its number.
  const myId = getSession()?.user.id;
  const userKey = myId != null ? String(myId) : 'self';
  const signature = core.accountSign(pickle, canonicalJson(deviceBody));

  const fallback = core.accountGenerateFallbackKey(pickle);
  await saveAccount(fallback.pickle);

  await api('/v1/keys/device/', {
    method: 'POST',
    body: {
      ...deviceBody,
      signatures: { [userKey]: { [`ed25519:${deviceId}`]: signature } },
      fallback_key_id: fallback.key_id,
      fallback_key: fallback.key,
      fallback_key_signature: core.accountSign(
        fallback.pickle,
        canonicalJson({ key: fallback.key }),
      ),
    },
  });

  await replenishOneTimeKeys();

  return { deviceId, curve25519: identity.curve25519, ed25519: identity.ed25519 };
}

/**
 * Top the prekey pool back up.
 *
 * Each key is single use: the server hands one out per session setup and
 * marks it claimed. Run dry and senders fall back to the reusable fallback
 * key, which still works but is weaker, so this is called at bootstrap and
 * should be called again when the server reports the pool running low.
 */
export async function replenishOneTimeKeys(): Promise<number> {
  const core = await loadCrypto();
  const pickle = await loadAccount();
  if (!pickle) throw new Error('No account to generate keys for.');

  const generated = core.accountGenerateOneTimeKeys(pickle, ONE_TIME_KEY_COUNT);

  // Each key signed individually over canonical JSON, so a recipient can
  // verify the one it claimed. Same shape as the phone: `{ key }`, not the
  // bare key string.
  const items = Object.entries(generated.one_time_keys).map(([key_id, key]) => ({
    key_id,
    key,
    signature: core.accountSign(generated.pickle, canonicalJson({ key })),
  }));

  await api('/v1/keys/onetime/', {
    method: 'POST',
    body: { device_id: getDeviceId(), one_time_keys: items },
  });

  // Only mark published AFTER the server accepted them. Marking first and
  // failing the upload would drop keys nobody can ever claim.
  const published = core.accountMarkKeysAsPublished(generated.pickle);
  await saveAccount(published);

  return items.length;
}
